import {
  composeClassNames,
  ifDefined,
  universalEach,
  withDefault,
} from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Slider from "react-slick";
import ContentSection from "src/components/ContentSection";
import InstructorCard from "src/components/InstructorCard";
import SectionHeading from "src/components/SectionHeading";
import SliderArrow from "src/components/SliderArrow";
import Translate from "src/components/Translate";
import constants from "src/constants";
import validateContent from "src/functions/validateContent";
import { sButton } from "src/styles/sButton.module.scss";
import { sInstructorSlider } from "src/styles/sInstructorSlider.module.scss";
import { ApiProfileCard } from "src/types/ApiProfileCard";

type Props = {
  btnTitle?: string | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiProfileCard[];
  style?: CSSProperties | null | undefined;
  subtitle: string;
  title: string;
  numberOfInstructors?: number;
};

function InstructorSlider({
  btnTitle,
  className,
  data,
  style,
  subtitle,
  title,
  numberOfInstructors,
}: Props) {
  const { push } = useRouter();

  return (
    <ContentSection
      style={style}
      className={composeClassNames([
        sInstructorSlider,
        className,
        "instructor-slider-wrapper",
      ])}
      fluid={`xxl`}
      verticalSpace={`none`}
    >
      <SectionHeading
        align="center"
        subtitle={subtitle}
        style={{ marginBottom: constants.Size.xl }}
        title={title}
      />
      {data && data.length > 0 && (
        <Slider
          arrows={true}
          infinite={true}
          speed={500}
          nextArrow={<SliderArrow direction={`next`} />}
          prevArrow={<SliderArrow direction={`prev`} />}
          slidesToShow={6}
          slidesToScroll={6}
          responsive={[
            {
              breakpoint: constants.Breakpoints.xl,
              settings: { slidesToShow: 5, slidesToScroll: 5 },
            },
            {
              breakpoint: constants.Breakpoints.lg,
              settings: { slidesToShow: 4, slidesToScroll: 4 },
            },
            {
              breakpoint: constants.Breakpoints.md,
              settings: { slidesToShow: 3, slidesToScroll: 3 },
            },
            {
              breakpoint: constants.Breakpoints.sm,
              settings: { slidesToShow: 2, slidesToScroll: 2 },
            },
          ]}
        >
          {data.length === 0 && <Translate tKey={"No instructors found"} />}
          {(() => {
            let validated = false;
            const instructors = constants.List.hpInstructors;
            const sortedInstructors = instructors.map(
              (instructorPosition) =>
                data &&
                Array.isArray(data) &&
                data.find(
                  (instructorId) =>
                    Number(instructorId.id) === instructorPosition
                )
            );
            return universalEach(sortedInstructors, (item) => {
              const instructor = instructors.find(
                (instructor) => instructor === Number(item?.id)
              );
              validated =
                instructor !== undefined &&
                validateContent(
                  withDefault(item?.blocked, true),
                  ifDefined(
                    item?.verified,
                    (value) => value.verified,
                    () => false
                  ),
                  withDefault(item?.private, false)
                );
              return validated ? (
                <InstructorCard
                  data={item}
                  key={`instructor-card-` + String(item.id)}
                />
              ) : null;
            });
          })()}
        </Slider>
      )}
      <div className={"show-all"}>
        <Button
          onClick={() => push(constants.Paths.pageProfiles)}
          className={composeClassNames([sButton])}
          variant={"outline-primary"}
        >
          {!btnTitle ? (
            <>
              <Translate tKey={`Get to know`} /> {numberOfInstructors}{" "}
              <Translate tKey={`instructors`} />
            </>
          ) : (
            <Translate tKey={btnTitle} />
          )}
        </Button>
      </div>
    </ContentSection>
  );
}

export default InstructorSlider;
